// ServicePaymentForm.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import useLocalStorageArray from '../hooks/useLocalStorageArray';
// import './ServicePaymentForm.css';

const ServicePaymentForm: React.FC = () => {
  // Form field states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [refundAddress, setRefundAddress] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('PSL010');

  // For networks and tokens (similar to SellTransactionForm)
  const [networks, setNetworks] = useState<any[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<any | null>(null);
  const [tokens, setTokens] = useState<any[]>([]);
  const [selectedToken, setSelectedToken] = useState<any | null>(null);

  // Informative quote
  const [quote, setQuote] = useState<any | null>(null); // To store the received quote

  // Additional states
  const [debounceTimeout, setDebounceTimeout] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);

  // For suggestions (autocomplete) using a custom hook
  const [prevPhoneNumbers, addPrevPhoneNumber] = useLocalStorageArray('paymentForm-phoneNumbers', []);
  const [prevEmails, addPrevEmail] = useLocalStorageArray('paymentForm-emails', []);
  const [prevRefundAddresses, addPrevRefundAddress] = useLocalStorageArray('paymentForm-refundAddresses', []);

  const navigate = useNavigate();

  // Fetch networks on mount
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const response = await api.get('/networks');
        setNetworks(response.data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching networks:', err);
        const errorMessage =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Failed to fetch networks. Please try again later.';
        setError(errorMessage);
      }
    };
    fetchNetworks();
  }, []);

  // Fetch tokens when a network is selected
  useEffect(() => {
    if (selectedNetwork) {
      const fetchTokens = async () => {
        try {
          const response = await api.get(`/networks/${selectedNetwork.id}/paymentAssets`);
          setTokens(response.data);
          setError(null);
        } catch (err: any) {
          console.error('Error fetching tokens:', err);
          const errorMessage =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Failed to fetch tokens. Please try again later.';
          setError(errorMessage);
        }
      };
      fetchTokens();
    }
  }, [selectedNetwork]);


  const validateWalletAddress = (address: string, network: string) => {
    if (!address) return true; // Don't flag an error for empty fields
    if (network === 'Solana') {
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaAddressRegex.test(address);
    } else if (
      network === 'Optimism Sepolia' ||
      network === 'Base' ||
      network === 'Base Sepolia' ||
      network === 'Optimism'
    ) {
      const evmAddressRegex = /^0x[a-fA-F0-9]{40}$/;
      return evmAddressRegex.test(address);
    }
    return false;
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    setRefundAddress(address)
  };

  // UseEffect to validate address whenever network or walletAddress changes
  useEffect(() => {
    if (refundAddress && selectedNetwork?.name) {
      if (!validateWalletAddress(refundAddress, selectedNetwork.name)) {
        setAddressError('Invalid wallet address for the selected network.');
      } else {
        setAddressError(null);
        addPrevRefundAddress(refundAddress)
      }
    } else {
      setAddressError(null); // No error if the wallet address is empty
    }
  }, [refundAddress, selectedNetwork]);

    // Fetch quote function (immediate call instead of debounce)
    // Debounced function to fetch the quote
  const fetchQuote = useCallback(() => {
    if (selectedPackage && selectedToken && selectedNetwork) {
      // Extract numeric value from the selected package (e.g., "PSL010" => 10)
      const numericValue = parseFloat(selectedPackage.replace("PSL", ""));
      
    setLoading(true);
    api
        .post('/quote', {
        toChain: selectedNetwork.name,
        toToken: selectedToken.symbol,
        amount: numericValue + 5.00,
        direction: 'buy',
        channel: 'services'
        })
        .then((response) => {
        setQuote(response.data.data);
        // setConversionRate(parseFloat(response.data.data.quoteRate));
        setLoading(false);
        })
        .catch((error) => {
        console.error('Error fetching quote:', error);
        setLoading(false);
        const errorMessage = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Failed to fetch quote. Please try again later.';
        setError(errorMessage);
        });
    }
  }, [selectedPackage, selectedNetwork, selectedToken]);

  useEffect(() => {
    if (selectedPackage && selectedToken && selectedNetwork) {

        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }
        const timeout = setTimeout(() => fetchQuote(), 500);
        setDebounceTimeout(timeout);
        return () => clearTimeout(timeout);
    }
  }, [selectedPackage, selectedToken, selectedNetwork, fetchQuote]);

  // Handler for form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation for required fields
    if (!phoneNumber.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (!selectedNetwork || !selectedToken) {
      setError('Please select a network and token.');
      return;
    }
    if (!selectedPackage) {
        setError('Please select a package.');
        return;
      }

    // Extract numeric value from the selected package (e.g., "PSL010" => 10)
    const numericValue = parseFloat(selectedPackage.replace(/^PSL0*/, ''));

    // Prepare data to send
    const paymentData = {
      phoneNumber,
      email, // optional, for notifying result
      network: selectedNetwork.name,
      token: selectedToken.symbol,
      amount: numericValue + 5.00,
      package: selectedPackage,
      refundAddress, // optional, for refunding change or whole amount
    };

    setLoading(true);
    try {
      const response = await api.post('/pay/service', paymentData);
      setLoading(false);
      // Optionally clear errors
      setError(null);

      // Save inputs for autocomplete suggestions
      addPrevPhoneNumber(phoneNumber);
      if (email.trim()) addPrevEmail(email);
      if (refundAddress.trim()) addPrevRefundAddress(refundAddress);

      // Navigate to a success page or display a success message
      navigate(`/payments/${response.data.transaction.paymentId}`);
    } catch (err: any) {
      setLoading(false);
      console.error('Error processing payment:', err);
      const errorMessage =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to process payment. Please try again later.';
      setError(errorMessage);
    }
  };

  // Check if form is valid for enabling the "Let's Start" button
  const isFormValid = phoneNumber && selectedNetwork.id && selectedToken.id && !addressError;

  return (
    <form onSubmit={handleSubmit}>
      <div className="transaction-form">
        <h2>Pago de Servicios</h2>

        {/* Phone Number Field */}
        <div className="form-group">
          <label>Phone Number<span className="required">*</span></label>
          <input
            type="tel"
            list="phone-suggestions"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Enter your phone number"
            required
          />
          <datalist id="phone-suggestions">
            {prevPhoneNumbers.map((val, idx) => (
              <option key={idx} value={val} />
            ))}
          </datalist>
        </div>

        {/* Email Field (optional) */}
        <div className="form-group">
          <label>
            Email <span className="optional">(for notifying result)</span>
          </label>
          <input
            type="email"
            list="email-suggestions"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email (optional)"
          />
          <datalist id="email-suggestions">
            {prevEmails.map((val, idx) => (
              <option key={idx} value={val} />
            ))}
          </datalist>
        </div>

        {/* Network Selector */}
        <div className="form-group">
          <label>Select Network</label>
          <select
            value={selectedNetwork?.id || ''}
            onChange={(e) =>
              setSelectedNetwork(
                networks.find((network: any) => network.id === parseInt(e.target.value))
              )
            }
            required
          >
            <option value="">Select network</option>
            {networks.map((network: any) => (
              <option key={network.id} value={network.id}>
                {network.name}
              </option>
            ))}
          </select>
        </div>

        {/* Token Selector */}
        {selectedNetwork && (
          <div className="form-group">
            <label>Select Token</label>
            <select
              value={selectedToken?.id || ''}
              onChange={(e) =>
                setSelectedToken(
                  tokens.find((token: any) => token.id === parseInt(e.target.value))
                )
              }
              required
            >
              <option value="">Select token</option>
              {tokens.map((token: any) => (
                <option key={token.id} value={token.id}>
                  {token.name} ({token.symbol})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Package Selection */}
        <div className="form-group">
          <label>Package Selection</label>
          <select
            value={selectedPackage}
            onChange={(e) => setSelectedPackage(e.target.value)}
            required
          >
            <option value="PSL010">$10 - Paquete Sin Limite</option>
            <option value="PSL020">$20 - Paquete Sin Limite</option>
            <option value="PSL030">$30 - Paquete Sin Limite</option>
          </select>
        </div>

        {/* Refund Address Field (optional) */}
        <div className="form-group">
          <label>
            Refund Address <span className="optional">(for refunding whole amount or change if any. If empty will refund to sender address.)</span>
          </label>
          <input
            type="text"
            list="refund-suggestions"
            value={refundAddress}
            onChange={handleAddressChange}
            placeholder="Enter refund address (optional)"
          />
          <datalist id="refund-suggestions">
            {prevRefundAddresses.map((val, idx) => (
              <option key={idx} value={val} />
            ))}
          </datalist>
          {addressError && <span className="error-message">{addressError}</span>}
        </div>

        {/* Display any global error */}
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>Costo aproximado: </label>
          <div className="conversion-output">
            {loading ? (
              <div className="spinner"></div>
            ) : (
              <>
                <p>{quote ? `${quote.quoteAmount} ${selectedToken?.symbol}` : 'Enter amount to see conversion'}</p>
                <p>{quote ? `1 ${selectedToken?.symbol} ≈ ${quote.quoteRate} MXN` : ''}</p>
              </>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={`start-button ${!isFormValid ? 'disabled' : 'enabled'}`}
          disabled={!isFormValid}
        >
          Confirmar
        </button>
      </div>
    </form>
  );
};

export default ServicePaymentForm;
